import { endSession, isTerminalAuthError, refreshOnce } from '@shared/auth/client';
import { useSessionStore } from '@shared/auth/session';

import { endpoint, type ServiceKey } from './config';
import { ApiError } from './envelope';
import { httpRequest, type HttpRequestInit } from './http';

/**
 * The one authenticated client. It attaches the in-memory access token, sends
 * credentials so the host-only refresh cookie rides along, unwraps the
 * envelope, and retries a `401` **once** behind the shared refresh mutex.
 *
 * Features call these wrappers and never the raw generated module — the
 * generated types describe the payloads, this file owns the transport.
 *
 * The platform's idempotency header is deliberately absent: it is scoped to
 * finance-bound money-moving writes and this console runs none. Sending it
 * would document a contract that does not exist (PRD-TDD §10.3). Retry safety
 * here comes from the server instead — a unique constraint on a report
 * resolution, the held-state predicate on a comment decision, the state
 * predicate on both publish machines, and `confirm=true` on every hard removal.
 */

async function authorizedRequest<T>(
  service: ServiceKey,
  path: string,
  init: HttpRequestInit,
  allowRefresh: boolean,
): Promise<T> {
  const { accessToken } = useSessionStore.getState();

  const headers = new Headers(init.headers);
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);

  try {
    return await httpRequest<T>(endpoint(service, path), { ...init, headers });
  } catch (error) {
    const unauthorized = error instanceof ApiError && error.status === 401;

    if (!unauthorized) throw error;

    // A terminal code is the session ending, not a request to retry. The router
    // catches it on a navigation; nothing caught it on a refetch, so every panel
    // on an open page printed «sign in again» beside a Retry that could not work.
    if (isTerminalAuthError(error)) {
      endSession();
      throw error;
    }

    if (!allowRefresh) {
      // The retry came back 401 as well: the token this refresh minted is already
      // not good enough, so there is nothing left to try.
      endSession();
      throw error;
    }

    // One refresh, one retry. A refresh that itself fails — commonly
    // `AUTH_REFRESH_REPLAYED`, because a second tab spent the single-use cookie
    // first — ends the session here rather than surfacing as a panel error.
    try {
      await refreshOnce();
    } catch (refreshError) {
      endSession();
      throw refreshError;
    }

    return authorizedRequest<T>(service, path, init, false);
  }
}

function withBody(method: string, body: unknown, init: HttpRequestInit): HttpRequestInit {
  return { ...init, method, ...(body !== undefined ? { body } : {}) };
}

export const api = {
  get: <T>(service: ServiceKey, path: string, init: HttpRequestInit = {}): Promise<T> =>
    authorizedRequest<T>(service, path, { ...init, method: 'GET' }, true),

  post: <T>(service: ServiceKey, path: string, body?: unknown, init: HttpRequestInit = {}): Promise<T> =>
    authorizedRequest<T>(service, path, withBody('POST', body, init), true),

  patch: <T>(service: ServiceKey, path: string, body?: unknown, init: HttpRequestInit = {}): Promise<T> =>
    authorizedRequest<T>(service, path, withBody('PATCH', body, init), true),

  put: <T>(service: ServiceKey, path: string, body?: unknown, init: HttpRequestInit = {}): Promise<T> =>
    authorizedRequest<T>(service, path, withBody('PUT', body, init), true),

  /**
   * `confirm=true` is the wire half of the dialog. Every hard removal requires
   * it and the console sets it **only** from a `<ConfirmDialog>` accept
   * handler — a code path that sets it from anywhere else has removed the
   * guard while leaving the dialog on screen (PRD-TDD §10.3).
   */
  delete: <T>(service: ServiceKey, path: string, init: HttpRequestInit = {}): Promise<T> =>
    authorizedRequest<T>(service, path, { ...init, method: 'DELETE' }, true),
};

export { ApiError } from './envelope';
export type { ApiErrorDetail } from './envelope';
export { getLastRequestId } from './http';
